"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { Badge } from "./ui/badge";
import { 
  Upload, 
  FileText, 
  X, 
  CheckCircle, 
  AlertTriangle,
  User,
  GraduationCap,
  Camera,
  MessageCircle,
  RefreshCw,
  ArrowRight,
  ArrowLeft,
  Edit,
  Eye,
  Clock,
  PartyPopper
} from "lucide-react";
const openTurfLogo = '/openturf-logo.svg';

interface UploadedFile {
  id: string;
  file: File;
  preview: string;
  type: string;
}

interface CorrectionItem {
  id: string;
  category: string;
  title: string;
  icon: React.ElementType;
  hrComment: string;
  acceptedTypes: string;
  maxFiles: number;
  isRequired: boolean;
  status: "pending" | "completed";
  briefReason: string;
}

interface DocumentCorrectionProps {
  onComplete?: () => void;
  onShowTerms?: () => void;
  onBack?: () => void;
}

// Mock correction data
const mockCorrections: CorrectionItem[] = [
  {
    id: "pan-card",
    category: "Identity Documents",
    title: "PAN Card",
    icon: User,
    hrComment: "Please upload a clearer copy of your PAN card. The current image is blurry and we can't read the PAN number clearly.",
    acceptedTypes: ".pdf,.jpg,.jpeg,.png",
    maxFiles: 1,
    isRequired: true,
    status: "pending",
    briefReason: "Image quality too low - PAN number not readable"
  },
  {
    id: "degree-certificate",
    category: "Educational Certificates", 
    title: "Degree Certificate",
    icon: GraduationCap,
    hrComment: "Your degree certificate appears to be missing the university seal. Please upload a copy with the official seal visible.",
    acceptedTypes: ".pdf,.jpg,.jpeg,.png",
    maxFiles: 1,
    isRequired: true,
    status: "pending",
    briefReason: "Missing university seal for verification"
  },
  {
    id: "profile-photo",
    category: "Professional Photo",
    title: "Profile Photo",
    icon: Camera,
    hrComment: "The uploaded photo doesn't meet our professional standards. Please upload a clear headshot with a plain background.",
    acceptedTypes: ".jpg,.jpeg,.png",
    maxFiles: 1,
    isRequired: false,
    status: "pending",
    briefReason: "Photo doesn't meet professional standards"
  }
];

type WorkflowStep = "overview" | "correction" | "review" | "success" | "error";

export function DocumentCorrection({ onComplete, onShowTerms, onBack }: DocumentCorrectionProps) {
  const [currentStep, setCurrentStep] = React.useState<WorkflowStep>("overview");
  const [currentCorrectionIndex, setCurrentCorrectionIndex] = React.useState(0);
  const [corrections, setCorrections] = React.useState<CorrectionItem[]>(mockCorrections);
  const [uploadedFiles, setUploadedFiles] = React.useState<Record<string, UploadedFile[]>>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);

  const completedCorrections = corrections.filter(c => c.status === "completed").length;
  const totalCorrections = corrections.length;
  const requiredCorrections = corrections.filter(c => c.isRequired);
  const completedRequired = requiredCorrections.filter(c => c.status === "completed").length;

  const handleStartCorrections = React.useCallback(() => {
    setCurrentStep("correction");
    setCurrentCorrectionIndex(0);
  }, []);

  const handleFileUpload = (files: FileList | null, correctionId: string) => {
    if (!files) return;

    const correction = corrections.find(c => c.id === correctionId)!;
    const currentFiles = uploadedFiles[correctionId] || [];
    
    if (currentFiles.length >= correction.maxFiles) {
      alert(`Maximum ${correction.maxFiles} file(s) allowed for this correction.`);
      return;
    }

    const newFiles: UploadedFile[] = [];
    const remainingSlots = correction.maxFiles - currentFiles.length;
    
    for (let i = 0; i < Math.min(files.length, remainingSlots); i++) {
      const file = files[i];
      const fileId = `${correctionId}-${Date.now()}-${i}`;
      
      newFiles.push({
        id: fileId,
        file,
        preview: URL.createObjectURL(file),
        type: file.type
      });
    }

    setUploadedFiles(prev => ({
      ...prev,
      [correctionId]: [...currentFiles, ...newFiles]
    }));

    // Mark correction as completed when files are uploaded
    setCorrections(prev => prev.map(c => 
      c.id === correctionId ? { ...c, status: "completed" } : c
    ));
  };

  const removeFile = (correctionId: string, fileId: string) => {
    setUploadedFiles(prev => ({
      ...prev,
      [correctionId]: (prev[correctionId] || []).filter(file => file.id !== fileId)
    }));

    // Mark as pending if no files left
    const remainingFiles = (uploadedFiles[correctionId] || []).filter(file => file.id !== fileId);
    if (remainingFiles.length === 0) {
      setCorrections(prev => prev.map(c => 
        c.id === correctionId ? { ...c, status: "pending" } : c
      ));
    }
  };

  const handleNextCorrection = () => {
    if (currentCorrectionIndex < corrections.length - 1) {
      setCurrentCorrectionIndex(currentCorrectionIndex + 1);
    } else {
      setCurrentStep("review");
    }
  };

  const handlePreviousCorrection = () => {
    if (currentCorrectionIndex > 0) {
      setCurrentCorrectionIndex(currentCorrectionIndex - 1);
    } else {
      setCurrentStep("overview");
    }
  };

  const handleSubmitAll = async () => {
    if (completedRequired < requiredCorrections.length) {
      alert("Please complete all required corrections before submitting.");
      return;
    }

    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    
    // Simulate success/failure
    const isSuccess = Math.random() > 0.2; // 80% success rate
    setCurrentStep(isSuccess ? "success" : "error");
    
    if (isSuccess) {
      setTimeout(() => {
        onComplete?.();
      }, 4000);
    }
  };

  const currentCorrection = corrections[currentCorrectionIndex];

  // Overview Screen
  if (currentStep === "overview") {
    return (
      <OverviewScreen
        corrections={corrections}
        onStart={handleStartCorrections}
        onBack={onBack}
        onShowTerms={onShowTerms}
      />
    );
  }

  // Individual Correction Screen
  if (currentStep === "correction") {
    return (
      <CorrectionScreen
        correction={currentCorrection}
        correctionIndex={currentCorrectionIndex}
        totalCorrections={totalCorrections}
        uploadedFiles={uploadedFiles[currentCorrection.id] || []}
        onFileUpload={(files) => handleFileUpload(files, currentCorrection.id)}
        onRemoveFile={(fileId) => removeFile(currentCorrection.id, fileId)}
        onNext={handleNextCorrection}
        onPrevious={handlePreviousCorrection}
        onShowTerms={onShowTerms}
        dragOver={dragOver}
        setDragOver={setDragOver}
      />
    );
  }

  // Review Screen
  if (currentStep === "review") {
    return (
      <ReviewScreen
        corrections={corrections}
        uploadedFiles={uploadedFiles}
        onSubmit={handleSubmitAll}
        onBack={() => setCurrentStep("correction")}
        onEdit={(correctionId) => {
          const index = corrections.findIndex(c => c.id === correctionId);
          setCurrentCorrectionIndex(index);
          setCurrentStep("correction");
        }}
        onShowTerms={onShowTerms}
        isSubmitting={isSubmitting}
      />
    );
  }

  // Success Screen
  if (currentStep === "success") {
    return <SuccessScreen onShowTerms={onShowTerms} />;
  }

  // Error Screen
  if (currentStep === "error") {
    return (
      <ErrorScreen 
        onRetry={() => setCurrentStep("review")}
        onShowTerms={onShowTerms}
      />
    );
  }

  return null;
}

// Overview Screen Component
function OverviewScreen({ 
  corrections, 
  onStart, 
  onBack, 
  onShowTerms 
}: {
  corrections: CorrectionItem[];
  onStart: () => void;
  onBack?: () => void;
  onShowTerms?: () => void;
}) {
  
  // Debug function to test button click
  const handleStartClick = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Start button clicked!'); // Debug log
    onStart();
  }, [onStart]);
  const requiredCount = corrections.filter(c => c.isRequired).length;
  const optionalCount = corrections.length - requiredCount;

  return (
    <div className="h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex flex-col relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-32 h-32 bg-purple-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-purple-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-radial from-purple-100/10 to-transparent rounded-full" />
      </div>

      {/* Fixed Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 p-6 bg-white/50 backdrop-blur-sm border-b border-gray-200/50 flex-shrink-0"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img 
            src={openTurfLogo} 
            alt="OpenTurf Technologies" 
            className="h-8 w-auto"
          />
          {onBack && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documents
            </Button>
          )}
        </div>
      </motion.header>

      {/* Scrollable Main Content */}
      <div className="flex-1 overflow-y-auto flex items-center justify-center p-6 relative z-10">
        <div className="max-w-2xl mx-auto w-full relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="w-16 h-16 bg-gradient-to-r from-orange-400 to-orange-500 rounded-2xl mx-auto flex items-center justify-center mb-6">
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
            
            <h1 className="text-gray-900 mb-4">
              Oops, looks like we need a few tweaks. Don't worry — it's quick! 🔧
            </h1>
            
            <p className="text-gray-600 max-w-xl mx-auto mb-8">
              Our HR team has reviewed your documents and needs some clarifications. 
              Here's what needs your attention:
            </p>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="text-red-600 mb-1">{requiredCount}</div>
                <div className="text-sm text-red-800">Required Actions</div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="text-blue-600 mb-1">{optionalCount}</div>
                <div className="text-sm text-blue-800">Optional Improvements</div>
              </div>
            </div>
          </motion.div>

          {/* Corrections List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-lg border border-white/20 mb-8 relative z-10"
          >
            <h3 className="text-gray-900 mb-4">Actions Required</h3>
            <div className="space-y-3">
              {corrections.map((correction, index) => (
                <motion.div
                  key={correction.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl"
                >
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <correction.icon className="w-5 h-5 text-purple-600" />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="text-gray-900">{correction.title}</h4>
                      {correction.isRequired && (
                        <Badge variant="destructive" className="text-xs">
                          Required
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-1">{correction.category}</p>
                    <p className="text-xs text-orange-600">{correction.briefReason}</p>
                  </div>
                  
                  <div className="flex-shrink-0">
                    <Clock className="w-4 h-4 text-gray-400" />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Start Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-center relative z-20 pb-8"
          >
            <Button
              onClick={handleStartClick}
              className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 relative z-30 cursor-pointer pointer-events-auto"
              type="button"
            >
              Start Corrections
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-sm text-gray-500 mt-3">
              We'll guide you through each correction step by step
            </p>
          </motion.div>
        </div>
      </div>

      {/* Fixed Footer */}
      <motion.footer className="relative z-10 p-6 border-t border-gray-200/50 bg-white/50 backdrop-blur-sm flex-shrink-0">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-purple-600 hover:text-purple-700 hover:underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}

// Individual Correction Screen Component
function CorrectionScreen({
  correction,
  correctionIndex,
  totalCorrections,
  uploadedFiles,
  onFileUpload,
  onRemoveFile,
  onNext,
  onPrevious,
  onShowTerms,
  dragOver,
  setDragOver
}: {
  correction: CorrectionItem;
  correctionIndex: number;
  totalCorrections: number;
  uploadedFiles: UploadedFile[];
  onFileUpload: (files: FileList | null) => void;
  onRemoveFile: (fileId: string) => void;
  onNext: () => void;
  onPrevious: () => void;
  onShowTerms?: () => void;
  dragOver: boolean;
  setDragOver: (value: boolean) => void;
}) {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const progress = ((correctionIndex + 1) / totalCorrections) * 100;
  const hasFiles = uploadedFiles.length > 0;

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    onFileUpload(e.dataTransfer.files);
  };

  return (
    <div className="h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex flex-col relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-32 h-32 bg-purple-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-purple-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-radial from-purple-100/10 to-transparent rounded-full" />
      </div>

      {/* Fixed Header with Progress */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 p-6 bg-white/50 backdrop-blur-sm border-b border-gray-200/50 flex-shrink-0"
      >
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-8 w-auto"
            />
            <div className="text-sm text-gray-600">
              {correctionIndex + 1} of {totalCorrections}
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Document Corrections</span>
              <span>{Math.round(progress)}% Complete</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </div>
      </motion.header>

      {/* Scrollable Main Content */}
      <div className="flex-1 overflow-y-auto flex items-center justify-center p-6">
        <div className="w-full max-w-2xl mx-auto">
          <motion.div
            key={correctionIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="relative z-10"
          >
            {/* Header */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.5 }}
                className="mb-6"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl mx-auto flex items-center justify-center mb-4">
                  <correction.icon className="w-8 h-8 text-white" />
                </div>
              </motion.div>
              
              <motion.h1 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="mb-4 text-gray-900"
              >
                Let's fix your {correction.title}
              </motion.h1>
              
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="flex justify-center mb-6"
              >
                <Badge variant="secondary" className="text-sm">
                  {correction.category}
                </Badge>
                {correction.isRequired && (
                  <Badge variant="destructive" className="text-sm ml-2">
                    Required
                  </Badge>
                )}
              </motion.div>
            </div>

            {/* HR Comment */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="bg-blue-50 border border-blue-200 rounded-3xl p-6 mb-8"
            >
              <div className="flex items-start space-x-3">
                <MessageCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-blue-800 mb-2">HR Team says:</p>
                  <p className="text-blue-700">{correction.hrComment}</p>
                </div>
              </div>
            </motion.div>

            {/* Upload Area */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-white/20 mb-8"
            >
              <div
                className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all ${
                  dragOver 
                    ? 'border-purple-400 bg-purple-50' 
                    : 'border-gray-300 hover:border-purple-300'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-700 mb-2">
                  Drag and drop your file here, or{" "}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="text-purple-600 hover:text-purple-700 underline"
                  >
                    browse
                  </button>
                </p>
                <p className="text-sm text-gray-500">
                  Maximum {correction.maxFiles} file(s) • Accepted formats: {correction.acceptedTypes}
                </p>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple={correction.maxFiles > 1}
                  accept={correction.acceptedTypes}
                  onChange={(e) => onFileUpload(e.target.files)}
                  className="hidden"
                />
              </div>

              {/* File Previews */}
              {uploadedFiles.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-gray-900 mb-4">Uploaded Files</h4>
                  <div className="space-y-3">
                    {uploadedFiles.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex-shrink-0">
                          {file.type.startsWith('image/') ? (
                            <img
                              src={file.preview}
                              alt={file.file.name}
                              className="w-12 h-12 object-cover rounded"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-purple-100 rounded flex items-center justify-center">
                              <FileText className="w-6 h-6 text-purple-600" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-900 truncate">
                            {file.file.name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {(file.file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <button
                          onClick={() => onRemoveFile(file.id)}
                          className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between mt-8">
                <Button
                  variant="ghost"
                  onClick={onPrevious}
                  className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
                
                <Button
                  onClick={onNext}
                  disabled={correction.isRequired && !hasFiles}
                  className="bg-purple-600 hover:bg-purple-700 text-white disabled:bg-gray-300"
                >
                  {correctionIndex === totalCorrections - 1 ? 'Review' : 'Next'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Fixed Footer */}
      <motion.footer className="relative z-10 p-6 border-t border-gray-200/50 bg-white/50 backdrop-blur-sm flex-shrink-0">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-purple-600 hover:text-purple-700 hover:underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}

// Review Screen Component
function ReviewScreen({
  corrections,
  uploadedFiles,
  onSubmit,
  onBack,
  onEdit,
  onShowTerms,
  isSubmitting
}: {
  corrections: CorrectionItem[];
  uploadedFiles: Record<string, UploadedFile[]>;
  onSubmit: () => void;
  onBack: () => void;
  onEdit: (correctionId: string) => void;
  onShowTerms?: () => void;
  isSubmitting: boolean;
}) {
  const completedCorrections = corrections.filter(c => c.status === "completed").length;
  const requiredCorrections = corrections.filter(c => c.isRequired);
  const completedRequired = requiredCorrections.filter(c => c.status === "completed").length;
  const canSubmit = completedRequired === requiredCorrections.length;

  return (
    <div className="h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex flex-col relative">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-32 h-32 bg-purple-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-purple-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-radial from-purple-100/10 to-transparent rounded-full" />
      </div>

      {/* Fixed Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 p-6 bg-white/50 backdrop-blur-sm border-b border-gray-200/50 flex-shrink-0"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img 
            src={openTurfLogo} 
            alt="OpenTurf Technologies" 
            className="h-8 w-auto"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Corrections
          </Button>
        </div>
      </motion.header>

      {/* Scrollable Main Content */}
      <div className="flex-1 overflow-y-auto p-6 relative z-10">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-green-500 rounded-2xl mx-auto flex items-center justify-center mb-6">
              <Eye className="w-8 h-8 text-white" />
            </div>
            
            <h1 className="text-gray-900 mb-4">
              Review Your Corrections ✅
            </h1>
            
            <p className="text-gray-600 max-w-2xl mx-auto">
              Perfect! You've addressed all the required items. Please review your corrections below before submitting.
            </p>
          </motion.div>

          {/* Summary Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center border border-white/20">
              <div className="text-purple-600 mb-1">{completedCorrections}</div>
              <div className="text-sm text-gray-600">Corrections Made</div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center border border-white/20">
              <div className="text-green-600 mb-1">{completedRequired}</div>
              <div className="text-sm text-gray-600">Required Completed</div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center border border-white/20">
              <div className="text-blue-600 mb-1">{Object.values(uploadedFiles).flat().length}</div>
              <div className="text-sm text-gray-600">Files Uploaded</div>
            </div>
          </motion.div>

          {/* Review Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-4 mb-8"
          >
            {corrections.map((correction, index) => (
              <motion.div
                key={correction.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * index }}
                className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-lg border border-white/20"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                      correction.status === "completed" 
                        ? "bg-green-100" 
                        : "bg-gray-100"
                    }`}>
                      {correction.status === "completed" ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : (
                        <correction.icon className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-gray-900">{correction.title}</h3>
                        {correction.isRequired && (
                          <Badge variant="destructive" className="text-xs">
                            Required
                          </Badge>
                        )}
                        <Badge 
                          variant={correction.status === "completed" ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {correction.status === "completed" ? "Completed" : "Pending"}
                        </Badge>
                      </div>
                      
                      {correction.status === "completed" && uploadedFiles[correction.id] && (
                        <div className="text-sm text-gray-600">
                          {uploadedFiles[correction.id].length} file(s) uploaded
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(correction.id)}
                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 ml-4"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Submit Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-center pb-8"
          >
            {canSubmit ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
                <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-3" />
                <h3 className="text-green-800 mb-2">Ready to Submit!</h3>
                <p className="text-sm text-green-700">
                  All required corrections have been completed. You can now resubmit your documents.
                </p>
              </div>
            ) : (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 mb-6">
                <AlertTriangle className="w-8 h-8 text-orange-600 mx-auto mb-3" />
                <h3 className="text-orange-800 mb-2">Missing Required Items</h3>
                <p className="text-sm text-orange-700">
                  Please complete all required corrections before submitting.
                </p>
              </div>
            )}

            <Button
              onClick={onSubmit}
              disabled={!canSubmit || isSubmitting}
              className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 disabled:bg-gray-300 relative z-50"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Resubmitting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Resubmit Documents
                </>
              )}
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Fixed Footer */}
      <motion.footer className="relative z-10 p-6 border-t border-gray-200/50 bg-white/50 backdrop-blur-sm flex-shrink-0">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-purple-600 hover:text-purple-700 hover:underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}

// Success Screen Component
function SuccessScreen({ onShowTerms }: { onShowTerms?: () => void }) {
  // Create floating confetti elements
  const confettiElements = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    delay: Math.random() * 3,
    duration: 3 + Math.random() * 2,
    x: Math.random() * 100,
    rotation: Math.random() * 360,
    color: ['bg-purple-500', 'bg-green-500', 'bg-blue-500', 'bg-yellow-500', 'bg-pink-500'][Math.floor(Math.random() * 5)]
  }));

  return (
    <div className="h-screen bg-gradient-to-br from-green-50 via-white to-purple-50 flex flex-col relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-32 h-32 bg-green-200/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-purple-200/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-radial from-green-100/10 to-transparent rounded-full animate-pulse" />
      </div>

      {/* Floating Confetti */}
      {confettiElements.map((confetti) => (
        <motion.div
          key={confetti.id}
          className={`absolute w-3 h-3 rounded-full ${confetti.color}`}
          style={{ left: `${confetti.x}%`, top: '-10px' }}
          initial={{ y: -50, rotate: 0, opacity: 0 }}
          animate={{ 
            y: window.innerHeight + 50, 
            rotate: confetti.rotation,
            opacity: [0, 1, 1, 0]
          }}
          transition={{
            delay: confetti.delay,
            duration: confetti.duration,
            ease: "easeOut",
            repeat: Infinity,
            repeatDelay: 4
          }}
        />
      ))}

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative z-10 text-center max-w-md mx-auto"
        >
          {/* Success Icon with Animation */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, duration: 0.5, type: "spring", stiffness: 200 }}
            className="w-24 h-24 bg-gradient-to-r from-green-500 to-green-600 rounded-full mx-auto flex items-center justify-center mb-8 relative"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.8, duration: 0.3 }}
            >
              <CheckCircle className="w-12 h-12 text-white" />
            </motion.div>
            
            {/* Pulsing ring effect */}
            <motion.div
              className="absolute inset-0 rounded-full border-4 border-green-300"
              initial={{ scale: 1, opacity: 0.8 }}
              animate={{ scale: 1.5, opacity: 0 }}
              transition={{ delay: 1, duration: 1.5, repeat: Infinity, repeatDelay: 1 }}
            />
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="text-gray-900 mb-4"
          >
            Fantastic! All Done! 🎉
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="text-gray-600 mb-8"
          >
            Your document corrections have been successfully submitted. Our HR team will review them and get back to you soon.
          </motion.p>

          {/* Success Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20 mb-8"
          >
            <h3 className="text-gray-900 mb-4">What happens next?</h3>
            <div className="text-left space-y-3 text-sm text-gray-600">
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.1, duration: 0.4 }}
                className="flex items-start space-x-3"
              >
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                <p>Your corrections will be reviewed within 24 hours</p>
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.3, duration: 0.4 }}
                className="flex items-start space-x-3"
              >
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                <p>You'll receive an email confirmation once approved</p>
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.5, duration: 0.4 }}
                className="flex items-start space-x-3"
              >
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                <p>We'll send you your joining details shortly after</p>
              </motion.div>
            </div>
          </motion.div>

          {/* Animated Balloons */}
          <motion.div className="absolute -top-20 -left-10">
            <motion.div
              animate={{ y: [-10, 10, -10] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="text-4xl"
            >
              🎈
            </motion.div>
          </motion.div>
          
          <motion.div className="absolute -top-16 -right-8">
            <motion.div
              animate={{ y: [10, -10, 10] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              className="text-3xl"
            >
              🎈
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Fixed Footer */}
      <motion.footer 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.7, duration: 0.6 }}
        className="relative z-10 p-6 border-t border-gray-200/50 bg-white/50 backdrop-blur-sm flex-shrink-0"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between w-full">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-purple-600 hover:text-purple-700 hover:underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}

// Error Screen Component
function ErrorScreen({ onRetry, onShowTerms }: { onRetry: () => void; onShowTerms?: () => void }) {
  return (
    <div className="h-screen bg-gradient-to-br from-red-50 via-white to-purple-50 flex flex-col relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-32 h-32 bg-red-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-purple-200/20 rounded-full blur-3xl" />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="relative z-10 text-center max-w-md mx-auto"
        >
          <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-red-600 rounded-full mx-auto flex items-center justify-center mb-6">
            <AlertTriangle className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="text-gray-900 mb-4">
            Oops! Something went wrong 😔
          </h1>
          
          <p className="text-gray-600 mb-8">
            We couldn't process your submission right now. Please try again in a moment.
          </p>

          <div className="space-y-4">
            <Button
              onClick={onRetry}
              className="bg-purple-600 hover:bg-purple-700 text-white w-full"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Fixed Footer */}
      <motion.footer className="relative z-10 p-6 border-t border-gray-200/50 bg-white/50 backdrop-blur-sm flex-shrink-0">
        <div className="max-w-4xl mx-auto flex items-center justify-between w-full">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-purple-600 hover:text-purple-700 hover:underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}